import React, { useEffect, useMemo, useState } from 'react';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { AutocompleteOption } from './AutoComplete';

export type AutoCompleteMultiSearchProps<T extends AutocompleteOption> = {
  label: string;
  placeholder: string;
  value: T[];
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: T[]) => void;
  collectOptions: (input: string) => Promise<T[]>;
  /** Nombre minimal de caractères (trim) avant de lancer `collectOptions`. */
  minSearchLength?: number;
  /** Délai avant l'appel à `collectOptions` après la dernière frappe. */
  debounceMs?: number;
  /** Texte affiché tant que la saisie est sous `minSearchLength`. */
  noOptionsTextTooShort?: string;
};

const DEFAULT_DEBOUNCE_MS = 500;

const AutoCompleteMultiSearch = <T extends AutocompleteOption>({
  label,
  placeholder,
  value,
  disabled = false,
  loading = false,
  onChange,
  collectOptions,
  minSearchLength = 0,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  noOptionsTextTooShort,
}: AutoCompleteMultiSearchProps<T>) => {
  const { t } = useTranslation(['random']);
  const theme = useTheme();
  const [options, setOptions] = useState<T[]>([]);
  const [inputValue, setInputValue] = useState('');

  const fetchOptions = useMemo(
    () =>
      debounce((searchText: string, callback: (results: T[]) => void) => {
        collectOptions(searchText).then(callback).catch(console.error);
      }, debounceMs),
    [collectOptions, debounceMs],
  );

  useEffect(() => {
    return () => {
      fetchOptions.cancel();
    };
  }, [fetchOptions]);

  useEffect(() => {
    const trimmedInput = inputValue.trim();
    if (disabled || trimmedInput.length < minSearchLength) {
      setOptions([]);
      fetchOptions.cancel();
      return undefined;
    }

    fetchOptions(trimmedInput, (results) => {
      setOptions(results);
    });

    return () => {
      fetchOptions.cancel();
    };
  }, [disabled, inputValue, fetchOptions, minSearchLength]);

  const resolvedNoOptionsText =
    trimmedInputBelowMin(inputValue, minSearchLength) && noOptionsTextTooShort
      ? noOptionsTextTooShort
      : t('noOptionsText');

  return (
    <Autocomplete
      multiple
      fullWidth
      size="small"
      disabled={disabled}
      loading={loading}
      loadingText={t('loadingText')}
      noOptionsText={resolvedNoOptionsText}
      options={options}
      value={value}
      filterOptions={(searchResults) => searchResults}
      getOptionLabel={(option: T) => option.name}
      isOptionEqualToValue={(left: T, right: T) => left.id === right.id}
      sx={{
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.blue[60],
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: theme.palette.blue[60],
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.blue[60],
        },
      }}
      onInputChange={(_event: React.SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      onChange={(_event: React.SyntheticEvent, newValue: T[]) => {
        onChange(newValue);
      }}
      renderTags={(selected: T[], getTagProps) =>
        selected.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} variant="outlined" label={option.name} {...tagProps} />;
        })
      }
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label} placeholder={placeholder} />
      )}
    />
  );
};

const trimmedInputBelowMin = (inputValue: string, minSearchLength: number): boolean => {
  const trimmedLength = inputValue.trim().length;
  return trimmedLength > 0 && trimmedLength < minSearchLength;
};

export { AutoCompleteMultiSearch };
export default AutoCompleteMultiSearch;
